import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuthStore } from '../../store/authStore'
import back_app from '../../lib/back_app'

export default function Header() {
  const navigate = useNavigate()
  const { logout: auth0_logout } = useAuth0()
  const { user, logout } = useAuthStore()
  const [menu_open, set_menu_open] = useState(false)
  const [switching, set_switching] = useState(false)
  const menu_ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!menu_open)
      return
    const on_click = (e: MouseEvent) => {
      if (menu_ref.current && !menu_ref.current.contains(e.target as Node))
        set_menu_open(false)
    }
    document.addEventListener('mousedown', on_click)
    return () => document.removeEventListener('mousedown', on_click)
  }, [menu_open])

  const handleLogout = () => {
    logout()
    auth0_logout({logoutParams: {returnTo: window.location.origin}})
  }

  const orgs = user?.orgs || {}
  const org_ids = Object.keys(orgs)
  const has_multi_orgs = org_ids.length > 1
  const current_org_id = user?.org_id_select || user?.org_id || ''
  const current_org_lbl = orgs[current_org_id]?.lbl || current_org_id

  const switch_org = async (org_id: string) => {
    if (switching || org_id == current_org_id) {
      set_menu_open(false)
      return
    }
    set_switching(true)
    try {
      const res = await back_app.user_set('org_id_select', org_id)
      if (res?.err && res.err != 'org_id_select_already') {
        console.error('switch org failed', res.err)
        set_switching(false)
        return
      }
      window.location.reload()
    } catch (err) {
      console.error('switch org failed', err)
      set_switching(false)
    }
  }

  const isCreator = user?.user_type === 'creator'

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <img
              src="/logo_sponly_color.svg"
              alt="Sponly"
              onClick={() => navigate(isCreator ? '/creator/dashboard' : '/brand/dashboard')}
              className="h-[31.68px] w-auto cursor-pointer"
            />

            <nav className="hidden md:flex items-center gap-6">
              {isCreator ? (
                <>
                  <button
                    onClick={() => navigate('/creator/dashboard')}
                    className="text-gray-700 hover:text-primary-blue font-medium transition"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/creator/upload')}
                    className="text-gray-700 hover:text-primary-blue font-medium transition"
                  >
                    Upload Video
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/brand/dashboard')}
                    className="text-gray-700 hover:text-primary-purple font-medium transition"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/brand/campaigns')}
                    className="text-gray-700 hover:text-primary-purple font-medium transition"
                  >
                    Campaigns
                  </button>
                  <button
                    onClick={() => navigate('/brand/assets')}
                    className="text-gray-700 hover:text-primary-purple font-medium transition"
                  >
                    Assets
                  </button>
                </>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative" ref={menu_ref}>
              <button
                onClick={() => set_menu_open(v => !v)}
                className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-100 transition"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-semibold text-dark leading-tight">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 leading-tight">
                    {has_multi_orgs ? current_org_lbl : user?.email}
                  </p>
                </div>
                <svg
                  className={'w-4 h-4 text-gray-500 transition-transform '
                    +(menu_open ? 'rotate-180' : '')}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 9l-7 7-7-7"/>
                </svg>
              </button>

              {menu_open && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white border border-gray-200
                    rounded-lg shadow-lg z-50 py-2"
                >
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-semibold text-dark truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>

                  {has_multi_orgs && (
                    <>
                      <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400
                        uppercase tracking-wide">
                        Switch organization
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {org_ids.map(oid => {
                          const is_current = oid == current_org_id
                          return (
                            <button
                              key={oid}
                              disabled={switching}
                              onClick={() => switch_org(oid)}
                              className={'w-full flex items-center justify-between px-4 py-2 '
                                +'text-left text-sm hover:bg-gray-50 transition '
                                +(is_current ? 'bg-gray-50 font-medium text-dark'
                                  : 'text-gray-700')}
                            >
                              <span className="truncate">{orgs[oid].lbl}</span>
                              {is_current && (
                                <svg className="w-4 h-4 text-primary-purple flex-shrink-0"
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round"
                                    strokeWidth={2} d="M5 13l4 4L19 7"/>
                                </svg>
                              )}
                            </button>
                          )
                        })}
                      </div>
                      <div className="border-t border-gray-100 my-2"/>
                    </>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700
                      hover:bg-gray-50 transition"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}